import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    ProductCategoryCreate as ProductCategoryCreateMutation,
    ProductCategoryCreateVariables as ProductCategoryCreateMutationVariables,
    ProductCategoryCreateInput,
} from 'nutshell-graphql-types';

import {bustManagedCollectionCache} from '../../../../util/bust-managed-collection-cache';

import CREATE_PRODUCT_CATEGORY from '../mutations/create-product-category.graphql';
import GET_PRODUCTS_CATEGORIES from '../queries/get-product-categories.graphql';

export function useCreateProductCategory(): {
    createProductCategory: (arg1: ProductCategoryCreateInput) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} {
    const [createProductCategory, {loading, error}] = useMutation<
        ProductCategoryCreateMutation,
        ProductCategoryCreateMutationVariables
    >(CREATE_PRODUCT_CATEGORY);

    return {
        createProductCategory: (input: ProductCategoryCreateInput) => {
            return createProductCategory({
                variables: {input},
                update: (proxy, {data}) => {
                    const productCategory =
                        data &&
                        data.productCategoryCreate &&
                        data.productCategoryCreate.productCategory;

                    if (!productCategory) return;

                    const existing = proxy.readQuery({
                        query: GET_PRODUCTS_CATEGORIES,
                    });

                    // @ts-expect-error
                    if (!existing || !existing.productCategories) return;

                    proxy.writeQuery({
                        query: GET_PRODUCTS_CATEGORIES,
                        data: {
                            // @ts-expect-error
                            productCategories: [...existing.productCategories, productCategory],
                            __typename: 'Query',
                        },
                    });
                },
            }).then((result) => {
                // Ensure REST-backed products lists refresh to include category changes
                bustManagedCollectionCache('products');
                bustManagedCollectionCache('productCategories');

                return (
                    result.data &&
                    result.data.productCategoryCreate &&
                    result.data.productCategoryCreate.productCategory
                );
            });
        },
        isLoading: loading,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
    };
}
